import React from 'react';
import round from 'lodash/round';
import Button from '@mui/material/Button';
import GeneralChart from '../GeneralChart';
import { CusEcharts } from '@/components/AsyncComponent';
import InfoTooltip from '../InfoTooltip';
import type { ECOption } from '@/components/CusEcharts';
import { CityContext } from '@/context/create-context';
import { numberToUpper } from '@/utils/numbers';
import { TAB_INDUSTRY_TREND } from '@/constants/city';
import { trackClickEvent } from '@/utils/site-track';

const Chart = ({
  month,
  salary,
  name,
}: {
  month: string[];
  salary: number[];
  name: string;
}) => {
  const chartRef = React.useRef(null);
  const parseIntMonth = month.map((item) => parseInt(item.split('-')[1]));
  const roundSalary = salary.map((item) => round(item, -2));

  const config = React.useMemo<{ config: ECOption; seoInfo: string }>(() => {
    const maxSalary = Math.max(...salary);
    const maxSalaryIndex = salary.indexOf(maxSalary);
    const getTrend = (data: number[]) => {
      return data[data.length - 1] - data[0] > 0 ? '上涨' : '下跌';
    };
    const trend =
      maxSalaryIndex === 0
        ? getTrend(salary.splice(0, 3))
        : maxSalaryIndex === month.length - 1
          ? getTrend(salary.splice(-3))
          : getTrend(salary.slice(maxSalaryIndex - 2, maxSalaryIndex + 1));

    return {
      seoInfo: `${name}行业近${numberToUpper(
        month.length,
      )}个月招聘市场的平均月薪如图：${parseInt(
        month[maxSalaryIndex].split('-')[1],
      )}月的平均月薪最高，为¥${round(
        maxSalary,
        -2,
      )}，相较前几个月的薪资有一定的${trend}。`,
      config: {
        grid: {
          show: true,
          tooltip: { show: true },
          left: 30,
          right: 30,
          top: 30,
          bottom: 20,
          containLabel: false,
          borderColor: '#f0f0f0',
          borderWidth: 1,
          shadowBlur: 0,
        },
        xAxis: {
          type: 'category',
          data: month,
          axisTick: { show: false },
          axisLine: { show: false },
          boundaryGap: false,
          axisLabel: {
            fontSize: 12,
            color: '#6e6e6e',
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'solid',
              color: '#f0f0f0',
            },
          },
        },
        yAxis: {
          type: 'value',
          axisTick: { show: false },
          axisLine: { show: false },
          axisLabel: { show: false },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'solid',
              color: '#f0f0f0',
            },
          },
        },
        tooltip: {
          show: false,
          trigger: 'axis',
          axisPointer: {
            type: 'line',
            lineStyle: {
              type: 'solid',
              color: '#0050B3',
            },
            label: {
              show: true,
              fontSize: 12,
              padding: [5, 4, 3],
            },
          },
        },
        series: [
          {
            type: 'line',
            data: roundSalary,
            smooth: true,
            symbol: 'circle',
            showSymbol: false,
            triggerLineEvent: false,
            color: '#0050B3',
            lineStyle: {
              width: 3,
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 12,
                color: '#2d2d2d',
                backgroundColor: 'transparent',
                formatter: ({ value }) => '¥' + value,
              },
            },
          },
        ],
      },
    };
  }, [month, salary]);

  return (
    <>
      <p className="text-sm font-semibold flex items-center">
        {`${
          parseIntMonth[parseIntMonth.length - 1]
        }月${name}类岗位平均月薪：¥${round(salary[salary.length - 1], -2)}`}
        <InfoTooltip className="ml-1 mt-[3px] shrink-0" />
      </p>
      <div className="w-full h-[235px] [&_canvas]:cursor-default">
        <CusEcharts chartRef={chartRef} config={config.config} />
      </div>
      <h4 className="mt-6">{config.seoInfo}</h4>
    </>
  );
};

const IndustryTrend = () => {
  const { avg_by_month: avgByMonth } = React.useContext(CityContext);
  const [industryId, setIndustryId] = React.useState<string>(avgByMonth[0].id);

  return (
    <GeneralChart
      title="热门岗位月薪趋势"
      subTip="追踪热门岗位的薪资波动，把握谈判入职的最佳时机。"
    >
      <h3 className="flex mb-4 whitespace-nowrap overflow-auto">
        {avgByMonth.map(({ id, name }, index) => {
          return (
            <Button
              key={index}
              onClick={(e) => {
                setIndustryId(id);
                id !== industryId && trackClickEvent(e);
              }}
              data-selected={id === industryId}
              className="!px-6 !py-2 !text-primary !bg-white/70 !font-semibold !text-sm !rounded-lg hover:!bg-black/4 data-[selected=true]:hover:!bg-blue-50 data-[selected=true]:!bg-web-blue-500/4 data-[selected=true]:!text-web-blue-500"
              data-ele-group={`${TAB_INDUSTRY_TREND}_industry`}
              data-ele-id={`industry_${name}`}
            >
              {name}
            </Button>
          );
        })}
      </h3>
      <div className="w-full">
        {avgByMonth
          .filter(({ id }) => id === industryId)
          .map(({ id, name, details }) => (
            <Chart
              key={id}
              month={Object.keys(details)}
              salary={Object.values(details)}
              name={name}
            />
          ))}
      </div>
    </GeneralChart>
  );
};

export default IndustryTrend;
